import { MapTankInfo, TankMapLatLong } from './../interfaces/mapTankInfo';

import { IServerSideGetRowsRequest } from '@ag-grid-community/core';
import { Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { IGridQueryResponse, IDynamicGridQueryResponse, IDynamicPortalEntity } from '@utils/iPortalEntity';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, map, switchMap, take, tap } from 'rxjs/operators';
import { Cacheable } from 'ts-cacheable';
import { AppState } from '../app.state';
import { ClientTankListDialogComponent } from '../components/client-tank-list-dialog/client-tank-list-dialog.component';
import { DSRESTClient } from '../shared/communication-objects/restclient.service';
import { api } from '../shared/communication-objects/api-path';
import { AG_GRID_LOCALE_ES } from '../shared/localization/ag-grid.locale.es';
import { selectUserInformation } from '../state/selectors/user-profile.selectors';
import { LogService } from './log.service';
import { SKIP_LOADING } from '@utils/loading-interceptor';
import { AppSettings } from '../shared/Constants/sharedConstants';

@Injectable()
export class GridService {

  private readonly apiUrl = api`Grid`;

  constructor(
    private restClient: DSRESTClient,
    private logger: LogService,
    private store: Store<AppState>,
    private translate: TranslateService,
    private dialog: MatDialog,
  ) {}

  getDefaultColOptions(): any[] {
    return [
      {
        headerName: '',
        field: 'selected',
        headerCheckboxSelection: false,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true,
        lockVisible: true,
        width: 35,
        suppressMenu: true,
        lockPinned: true,
        suppressMovable: true,
        suppressColumnsToolPanel: true,
        // Pinned by TankGridComponent only while the map is open.
        // No menu, sort or filter refs: this column has none of them.
        headerComponentParams: {
          template: `<div class="ag-cell-label-container" role="presentation">
            <div ref="eLabel" class="ag-header-cell-label" role="presentation">
          <div class="ag-wrapper ag-input-wrapper ag-checkbox-input-wrapper ag-checked" id="tank-grid-selectAll-wrapper" role="presentation" style="background-color:white">
                <input ref="eInput" id="tank-grid-selectAll" class="ag-input-field-input ag-checkbox-input" type="checkbox" checked="true">
          </div>
              <span ref="eText" class="ag-header-cell-text" role="columnheader"></span>
            </div>
          </div>`,
        },
      },
      {
        headerName: '',
        field: 'tankNotificationsCount',
        width: 35,
        // Pinned by TankGridComponent only while the map is open. No header menu: at 35px it crowds out the column.
        suppressMenu: true,
        cellRenderer: 'tankNotificationRenderer',
        suppressColumnsToolPanel: true,
      },
      {
        headerName: this.translate.instant('Column.GlobalTankSearch'),
        filter: 'globalSearchFilter',
        suppressMenu: true,
        lockPinned: true,
        lockVisible: true,
        hide: true,
        field: 'globalTankSearch',
        suppressColumnsToolPanel: true,
      },
      {
        headerName: this.translate.instant('Column.Skytank'),
        filter: 'skytankFilter',
        suppressMenu: true,
        lockPinned: true,
        lockVisible: true,
        hide: true,
        field: 'skytankFilter',
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: !AppSettings.UIAppConfig.features['skytank'],
      },
      {
        headerName: this.translate.instant('Column.RecentInstall'),
        filter: 'agTextColumnFilter',
        suppressMenu: true,
        hide: true,
        field: 'recentInstall',
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true,
      },
      {
        headerName: 'Delivery Efficiency',
        filter: 'agSetColumnFilter',
        field: 'deliveryEfficiency',
        suppressMenu: true,
        hide: true,
        filterParams: {
          values: [0, 1, 2, 3, 4, 5, 6],
          cellRenderer: (params: { value: string | number }) => {
            if (params.value === '(Select All)') return params.value;

            switch (params.value) {
              case '0':
                return this.translate.instant('TankGrid.Filters.30DaysBelow');
              case '1':
                return this.translate.instant('TankGrid.Filters.30DaysMet');
              case '2':
                return this.translate.instant('TankGrid.Filters.30DaysAbove');
              case '3':
                return this.translate.instant('TankGrid.Filters.60DaysBelow');
              case '4':
                return this.translate.instant('TankGrid.Filters.60DaysMet');
              case '5':
                return this.translate.instant('TankGrid.Filters.60DaysAbove');
              case '6':
                return this.translate.instant('TankGrid.Filters.AllOtherTanks');
            }
          },
          buttons: ['reset', 'apply'],
          suppressSelectAll: false,
          suppressMiniFilter: true,
        },
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: false,
      },
      {
        headerName: 'Least Efficient Deliveries',
        filter: 'agTextColumnFilter',
        suppressMenu: true,
        hide: true,
        field: 'leastEfficientDeliveries',
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true,
      },
      {
        headerName: 'No Recent Updates',
        filter: 'agTextColumnFilter',
        suppressMenu: true,
        hide: true,
        field: 'noRecentUpdate',
        suppressFiltersToolPanel: true,
        suppressColumnsToolPanel: true,
      },
      {
        headerName: 'Needs Delivery',
        filter: 'agTextColumnFilter',
        suppressMenu: true,
        hide: true,
        field: 'needsDelivery',
        suppressFiltersToolPanel: true,
        suppressColumnsToolPanel: true,
      },
    ];
    }

  // Not @Cacheable, for the same reason as getDynamic below: this is the row refresh a
  // DataChanged broadcast triggers, and it is called with the SAME uniqueIds every time the same
  // tank changes. ts-cacheable keys on the argument list with no maxAge, so the second change to
  // a tank replayed the first response and the row silently stopped updating for the session.
  // Columns are sent so the rows come back in the same shape getDynamic returned them in -
  // a refreshed row replaces one already in the grid, so a different key set would blank cells.
  GetTankGridDataByUniqueId(
    changedTankUniqueIds: number[],
    selectedColumns: string[],
    additionalColumns: string[] = [],
  ): Observable<IDynamicPortalEntity[]> {
    return this.restClient.post(
      api`${this.apiUrl}/GetTankGridDataByUniqueId`,
      {
        uniqueIds: changedTankUniqueIds,
        selectedColumns,
        additionalColumns,
      },
      new HttpContext().set(SKIP_LOADING, true)
    );
  }

  @Cacheable()
  getTankGridColumns(): Observable<any> {
    let results = this.restClient.get(api`${this.apiUrl}/GetTankGridColumns`);
    return results;
  }

  @Cacheable()
  get(params: any): Observable<IGridQueryResponse> {
    this.logger.log('calling server to get tank grid data', params);
    let apiResponse = this.restClient.postForResponse<IGridQueryResponse>(this.apiUrl, params);
    return apiResponse.pipe(
      tap((response) => {
        this.logger.log('get tank rows Response', response);
      })
    );
  }

  // Not @Cacheable: this is the ag-grid server-side datasource row query. It must hit the server
  // on every getRows, or a purge-refresh with unchanged params serves stale rows. Duplicate
  // getRows calls are suppressed upstream by pagingDataSource's ignoreNextGetRowsCall.
  getDynamic(params: any, selectedColumns: string[], additionalColumns?: string[]): Observable<IDynamicGridQueryResponse> {
    this.logger.log('calling server to get dynamic tank grid data', params, selectedColumns, additionalColumns);
    
    const request = {
      gridRequest: params,
      selectedColumns: selectedColumns,
      additionalColumns: additionalColumns || []
    };
    
    let apiResponse = this.restClient.postForResponse<IDynamicGridQueryResponse>(api`${this.apiUrl}/query-dynamic`, request);
    return apiResponse.pipe(
      tap((response) => {
        this.logger.log('get dynamic tank rows Response', response);
      })
    );
  }

  @Cacheable()
  getMapData(params: any): Observable<TankMapLatLong[]> {
    return this.restClient.post(api`${this.apiUrl}/GetMapData`, params).pipe(
      distinctUntilChanged(),
      map((result) => {
        return result;
      })
    );
  }

  @Cacheable()
  getMapDataExpanded(tankMapInfo: TankMapLatLong): Observable<MapTankInfo> {
    return this.restClient
      .getDirect<MapTankInfo>(
        api`${this.apiUrl}/GetMapDataExpanded/${tankMapInfo.tankID}`
      )
      .pipe(
        switchMap((result) => {
          const rtn = {
            tankID: tankMapInfo.tankID,
            uniqueID: result.uniqueID,
            latitude: tankMapInfo.latitude,
            longitude: tankMapInfo.longitude,
            formattedAddress: result.formattedAddress,
            tankName: result.tankName,
            volume: result.volume,
            uomSuffix: result.uomSuffix,
            specGrav: result.specGrav,
            inventoryPercent: result.inventoryPercent,
            daysToOrder: result.daysToOrder,
            daysToEmpty: result.daysToEmpty,
            product: result.product,
            active: result.active,
            volumeAlarmStatusType: tankMapInfo.volumeAlarmStatusType,
            tankCap: result.tankCap,
            photoUrl: result.photoUrl,
            rtuNumber: result.rtuNumber,
            showAsInactive: tankMapInfo.showAsInactive ?? result.showAsInactive,
            locationName: result.locationName
          };
          return of(rtn);
        })
      );
  }
  @Cacheable()
  getVolumeAlarmStatus(params: any): Observable<any> {
    return this.restClient.post(api`${this.apiUrl}/VolumeAlarmStatuses`, params);
  }
  @Cacheable()
  getSensorStatus(params: any): Observable<any> {
    return this.restClient.post(api`${this.apiUrl}/SensorAlarmStatuses`, params);
  }
  @Cacheable()
  getCommunicationStatus(params: any): Observable<any> {
    return this.restClient.post(
      api`${this.apiUrl}/CommunicationAlarmStatuses`,
      params
    );
  }
  @Cacheable()
  getMaintenanceStatus(params: any): Observable<any> {
    return this.restClient.post(
      api`${this.apiUrl}/MaintenanceAlarmStatuses`,
      params
    );
  }
  @Cacheable()
  getTankAlarmStatuses(params: any): Observable<any> {
    return this.restClient.post(api`${this.apiUrl}/TankAlarmStatuses`, params);
  }

  @Cacheable()
  getStates(): Observable<any> {
    return this.restClient.get(api`${this.apiUrl}/States`);
  }

  @Cacheable()
  getOrganizations(): Observable<any> {
    return this.restClient.get(api`${this.apiUrl}/Organizations`);
  }
  exportToCSV(params: any): Observable<any> {
    return this.restClient.postFile(api`${this.apiUrl}/ExportToCSV`, params);
  }
  exportToExcel(params: any): Observable<any> {
    return this.restClient.postFile(api`${this.apiUrl}/ExportToExcel`, params);
  }

  @Cacheable()
  getAlarmStatus(): Observable<any> {
    return this.restClient.get(api`${this.apiUrl}/GetAlarmStatus`);
  }

  @Cacheable()
  getRtuList(): Observable<any> {
    let apiResponse = this.restClient.get(api`${this.apiUrl}/RtuList`);
    return apiResponse.pipe(
      tap((response) => this.logger.log('getRtuList Response', response))
    );
  }

  @Cacheable()
  getMinList(): Observable<any> {
    let apiResponse = this.restClient.get(api`${this.apiUrl}/MinList`);
    return apiResponse.pipe(
      tap((response) => this.logger.log('getMinList Response', response))
    );
  }

  @Cacheable()
  getDropdowns(filterModel: IServerSideGetRowsRequest): Observable<any> {
    let apiResponse = this.restClient.postForResponse<any>(api`${this.apiUrl}/dashboard-dropdowns`, filterModel);
    return apiResponse.pipe(
      tap((response) => this.logger.log('getDropdowns Response', response))
    );
  }

  @Cacheable()
  getLocations(): Observable<any> {
    let apiResponse = this.restClient.get(api`${this.apiUrl}/Locations`);
    return apiResponse.pipe(
      tap((response) => this.logger.log('getLocationNames Response', response))
    );
  }

  @Cacheable()
  getGeolocationList(): Observable<any> {
    let apiResponse = this.restClient.get(api`${this.apiUrl}/GeolocationList`);
    return apiResponse.pipe(
      tap((response) => this.logger.log('GeolocationList Response', response))
    );
  }

  @Cacheable()
  getProducts(): Observable<any> {
    let apiResponse = this.restClient.get(api`${this.apiUrl}/Products`);
    return apiResponse;//.pipe(
      //tap((response) => this.logger.log('getProductNames Response', response))
    //);
  }

  @Cacheable()
  getRegions(): Observable<any> {
    let apiResponse = this.restClient.get(api`${this.apiUrl}/Regions`);
    return apiResponse.pipe(
      tap((response) => this.logger.log('getRegions Response', response))
    );
  }

  @Cacheable()
  getRoutes(): Observable<any> {
    let apiResponse = this.restClient.get(api`${this.apiUrl}/Routes`);
    return apiResponse.pipe(
      tap((response) => this.logger.log('getRoutes Response', response))
    );
  }


  getActiveFilterSet(): Observable<any> {
    return of(['Active', 'Inactive']);
  }

  @Cacheable()
  getTankNotifications(params: any): Observable<any> {
    let apiResponse = this.restClient.post(
      api`${this.apiUrl}/GetTankNotifications`,
      params
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('Grid/GetTankNotifications Response', response)
      )
    );
  }

  @Cacheable()
  getTankNotificationsByTankId(tankId: number) {
    let apiResponse = this.restClient.post(
      api`${this.apiUrl}/getTankNotificationsByTankId`,
      tankId
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('Grid/getTankNotificationsByTankId Response', response)
      )
    );
  }

  tankNotifications_DismissByHMAIdList(params: any): Observable<any> {
    let apiResponse = this.restClient.post(
      api`${this.apiUrl}/TankNotifications_DismissByHMAIdList`,
      params
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log(
          'Grid/TankNotifications_DismissByHMAIdList Response',
          response
        )
      )
    );
  }

  tankNotifications_RestoreAll(params: any): Observable<any> {
    let apiResponse = this.restClient.post(
      api`${this.apiUrl}/TankNotifications_RestoreAll`,
      params
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('Grid/TankNotifications_RestoreAll Response', response)
      )
    );
  }

  tankNotifications_RestoreAllByTankIdList(tankId: number) {
    let apiResponse = this.restClient.post(
      api`${this.apiUrl}/tankNotifications_RestoreAllByTankIdList`,
      tankId
    );
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log(
          'Grid/tankNotifications_RestoreAllByTankIdList Response',
          response
        )
      )
    );
  }

  formatLocation(location: IClientTankHost): string {
    return `${location.locationName} / ${location.locationId} / ${location.locationAddress1} ${location.locationAddress2 ? location.locationAddress2 : ''} ${location.locationCity ? location.locationCity : ''} ${location.locationState ? location.locationState : ''}`.trim().replace(/\/\s*$/, '');
  }

  getAGGrid_Locale_Text() {
    //get user's culture code
    let localeText: any;
    this.store
      .select(selectUserInformation)
      .pipe(take(1))
      .subscribe((userInfo) => {
        if (userInfo.cultureCode === 'es-MX') localeText = AG_GRID_LOCALE_ES;
      });
    return localeText;
  }

  getClientTankList(devId: number, includeSkytankSensors: boolean = false): Observable<IClientTankHost> {
    let apiResponse = this.restClient.post(api`${this.apiUrl}/getClientTankList`, { devId: devId, includeSkytankSensors: includeSkytankSensors });
    return apiResponse.pipe(
      tap((response) =>
        this.logger.log('Grid/getClientTankList Response', response)
      ),
      map(response => {
        return {
          ...response,
            locationFormatted: this.formatLocation(response),
            clientTankList: response.clientTankList?.map(tank => ({
              ...tank,
              locationFormatted: this.formatLocation(tank)
            }))
        };
      })
    );
  }

  showClientTankListDialog(tankInfo: IClientTankHost, dialogType: string): MatDialogRef<ClientTankListDialogComponent> {
    return this.dialog.open(ClientTankListDialogComponent, {
      panelClass: 'cso-dialog',
      width: 'unset',
      maxWidth: '100%',
      data: { tankInfo, dialogType: dialogType },
    })
  }

}

export interface ITankInfo {
  tankID: number;
  uniqueID?: number;
  tankName?: string;
  locationName?: string;
  active?: boolean;
}

export interface IClientTankHost {
  hostName: string;
  hostUniqueId: number;
  hostRtu: number;
  skyTankSensorCount: number;
  st90Count: number;
  hostDeviceModel: string;
  callScheduleStart: string | null;
  clientTankList: IClientTankDevice[];
  locationAddress1: string;
  locationAddress2: string;
  locationCity: string;
  locationState: string;
  locationId: number | null;
  locationName: string;
  locationFormatted?: string;
  initiatingDeviceId: number;
}

export interface IClientTankDevice {
  uniqueId: number;
  tankName: string;
  rtuNumber: number;
  min0: number;
  deviceActive: boolean;
  statusString: string;
  deviceModel: string;
  lastInventoryTimeConverted?: string;
  locationFormatted?: string;
  locationAddress1: string;
  locationAddress2: string;
  locationCity: string;
  locationState: string;
  locationId: number | null;
  locationName: string;
  locationIsReceiving: boolean;
  deviceCategory: number;
  tankId: number;
  deviceId: number;
}
